import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { STORAGE_KEYS } from '@/constants/storage';
import type { StoreResponse } from '@/types/store';
import type { CompanyResponse } from '@/types/company';
import { bootstrapSession, refreshSessionInit } from '@/store/sessionSlice';
import type { SessionInitResponse } from '@/types/session';
import { clearWorkspaceScopeStorage, markWorkspaceScopeReady } from '@/utils/workspaceScope';

export type ThemeMode = 'light' | 'dark';

interface AppState {
  selectedStore: StoreResponse | null;
  selectedCompany: CompanyResponse | null;
  theme: ThemeMode;
  sidebarCollapsed: boolean;
  /** Bumped when org modules change so layouts reload navigation */
  navModulesVersion: number;
  /** Bumped when selected company changes so scoped pages refetch */
  companyScopeVersion: number;
}

function loadTheme(): ThemeMode {
  const t = localStorage.getItem(STORAGE_KEYS.THEME);
  return t === 'dark' ? 'dark' : 'light';
}

function syncThemeClass(theme: ThemeMode) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

syncThemeClass(loadTheme());

function loadStore(): StoreResponse | null {
  const raw = localStorage.getItem(STORAGE_KEYS.SELECTED_STORE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoreResponse;
  } catch {
    return null;
  }
}

function loadCompany(): CompanyResponse | null {
  const raw = localStorage.getItem(STORAGE_KEYS.SELECTED_COMPANY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CompanyResponse;
  } catch {
    return null;
  }
}

function applySessionScopeToApp(state: AppState, payload: SessionInitResponse) {
  const companies = payload.companyScope?.companies ?? [];
  const stores = payload.stores ?? [];
  const companyIds = new Set(companies.map((c) => c.id));
  const storeIds = new Set(stores.map((s) => s.id));

  if (state.selectedCompany && !companyIds.has(state.selectedCompany.id)) {
    state.selectedCompany = null;
    localStorage.removeItem(STORAGE_KEYS.SELECTED_COMPANY);
  }
  if (state.selectedStore && !storeIds.has(state.selectedStore.id)) {
    state.selectedStore = null;
    localStorage.removeItem(STORAGE_KEYS.SELECTED_STORE);
  }

  if (!state.selectedCompany) {
    const activeId = payload.companyScope?.activeCompanyId ?? payload.companyScope?.defaultCompanyId;
    const match = activeId != null ? companies.find((c) => c.id === activeId) : undefined;
    if (match) {
      state.selectedCompany = match;
      localStorage.setItem(STORAGE_KEYS.SELECTED_COMPANY, JSON.stringify(match));
    } else if (companies.length === 1) {
      state.selectedCompany = companies[0];
      localStorage.setItem(STORAGE_KEYS.SELECTED_COMPANY, JSON.stringify(companies[0]));
    }
  }

  if (state.selectedCompany || state.selectedStore) {
    markWorkspaceScopeReady();
  }
}

const initialState: AppState = {
  selectedStore: loadStore(),
  selectedCompany: loadCompany(),
  theme: loadTheme(),
  sidebarCollapsed: false,
  navModulesVersion: 0,
  companyScopeVersion: 0,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setSelectedStore: (state, action: PayloadAction<StoreResponse | null>) => {
      state.selectedStore = action.payload;
      if (action.payload) {
        localStorage.setItem(STORAGE_KEYS.SELECTED_STORE, JSON.stringify(action.payload));
        markWorkspaceScopeReady();
      } else {
        localStorage.removeItem(STORAGE_KEYS.SELECTED_STORE);
      }
    },
    setSelectedCompany: (state, action: PayloadAction<CompanyResponse | null>) => {
      const previousId = state.selectedCompany?.id ?? null;
      const nextId = action.payload?.id ?? null;
      state.selectedCompany = action.payload;
      if (action.payload) {
        localStorage.setItem(STORAGE_KEYS.SELECTED_COMPANY, JSON.stringify(action.payload));
        markWorkspaceScopeReady();
      } else {
        localStorage.removeItem(STORAGE_KEYS.SELECTED_COMPANY);
      }
      if (previousId !== nextId) {
        state.companyScopeVersion += 1;
      }
    },
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.theme = action.payload;
      localStorage.setItem(STORAGE_KEYS.THEME, action.payload);
      document.documentElement.classList.toggle('dark', action.payload === 'dark');
    },
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },
    bumpNavModules: (state) => {
      state.navModulesVersion += 1;
    },
    resetWorkspaceScope: (state) => {
      state.selectedCompany = null;
      state.selectedStore = null;
      clearWorkspaceScopeStorage();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapSession.fulfilled, (state, action) => {
        applySessionScopeToApp(state, action.payload);
      })
      .addCase(refreshSessionInit.fulfilled, (state, action) => {
        applySessionScopeToApp(state, action.payload);
      })
      .addMatcher(
        (action) => action.type === 'auth/logout/fulfilled' || action.type === 'auth/clearAuth',
        (state) => {
          state.selectedCompany = null;
          state.selectedStore = null;
        },
      );
  },
});

export const {
  setSelectedStore,
  setSelectedCompany,
  setTheme,
  toggleSidebar,
  setSidebarCollapsed,
  bumpNavModules,
  resetWorkspaceScope,
} = appSlice.actions;
export default appSlice.reducer;
